/**
 * Segment van een leespaneeltekst: platte tekst, een bronverwijzing die als
 * voetnootnummer naar de bronnenlijst van de sectie wijst, of het label
 * "berekend" (`{calc}`): een uitspraak die uit het wiel of de tabel volgt en
 * geen letterlijke uitspraak van Ra is.
 */
export interface HdLocksKeysSourceRefSegment {
  readonly text: string;
  /** Nummers (1-gebaseerd) in de `sources`-lijst van de sectie, bij een verwijzing. */
  readonly refs?: readonly number[];
  readonly calc?: true;
}

const TOKEN = /\[(\d+(?:\s*,\s*\d+)*)\]|\{calc\}/g;

/**
 * Parseert een i18n-tekst met bronverwijzingen (`[1]`, `[2, 3]`) en
 * `{calc}`-labels naar segmenten voor de template. De nummers verwijzen naar
 * de `sources`-lijst van dezelfde sectie (`<sectionPrefix>.<key>.sources`);
 * de template toont ze als voetnootnummer.
 */
export function parseHdLocksKeysSourceRefs(text: string): readonly HdLocksKeysSourceRefSegment[] {
  if (text === '') {
    return [];
  }
  const segments: HdLocksKeysSourceRefSegment[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(TOKEN)) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index) });
    }
    if (match[1] == null) {
      segments.push({ text: '', calc: true });
    } else {
      const refs = match[1].split(',').map(part => Number(part.trim()));
      segments.push({ text: refs.join(', '), refs });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex) });
  }
  return segments;
}

/** Alle verwijzingsnummers in een tekst, in volgorde van voorkomen (voor de inhoudstests). */
export function hdLocksKeysSourceRefNumbers(text: string): readonly number[] {
  return parseHdLocksKeysSourceRefs(text).flatMap(segment => segment.refs ?? []);
}
